'use client'

import { useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { ContentContainer } from '@/components/ui/content-container'
import { cn } from '@/lib/utils'

/* ─── DATA ─── */

const SUPPORT_QUESTIONS = [
  {
    q: 'Will my partner see my answers?',
    a: 'Not while either of you is answering. After both of you finish, you receive the same written Brief. The Brief may name where your answers align, where they differ, and what each of you reported so the pattern is clear. The point is clarity, not scoring or judgment.',
  },
  {
    q: 'I was invited. What am I being asked to do?',
    a: 'You are being asked to answer your side of the same conversation privately. It usually takes 10 to 15 minutes. After both of you finish, you both receive the same written Brief to read together.',
  },
  {
    q: 'My partner has not started yet.',
    a: 'You can resend the invite from your dashboard or copy the invite link and send it directly. Your Brief appears after both of you answer the conversation.',
  },
  {
    q: 'What do we get for free?',
    a: 'You and your partner answer one conversation privately and receive one written Brief. No payment information required.',
  },
  {
    q: 'What is the difference between free and paid?',
    a: 'The first conversation is free. The full Check-In adds the other three conversations, conversation guides, your Marriage Roadmap, a downloadable PDF, and three Check-Ins over two years.',
  },
  {
    q: 'How long does it take?',
    a: 'One conversation usually takes 10 to 15 minutes each. The full Check-In has four conversations, and you do not have to finish everything in one sitting.',
  },
  {
    q: 'Are we being judged?',
    a: 'No. There is no grade, verdict, or compatibility score. The Brief names patterns and differences so you can have one clearer conversation.',
  },
  {
    q: 'What if the Brief is hard to read?',
    a: 'It may name something you have both been circling around. The Brief does not judge your relationship or tell you what to do. It gives you one clearer conversation to have next.',
  },
  {
    q: 'Can we take it more than once?',
    a: 'Yes. Your purchase includes up to three Check-Ins for the same couple, so you can revisit as your relationship changes.',
  },
  {
    q: 'Can I download my results?',
    a: 'Yes. You can download a PDF from your results page.',
  },
  {
    q: 'What if we are already in therapy?',
    a: 'The Check-In is not a replacement for therapy. It can give you a clearer starting point for a future conversation or session, if that feels useful to both of you.',
  },
]

/* ─── ANIMATION ─── */

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0 },
}

const viewport = { once: true, margin: '-60px' }

/* ─── COMPONENTS ─── */

function FaqItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false)

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={viewport}
      transition={{ duration: 0.4, ease: 'easeOut', delay: index * 0.04 }}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'w-full text-left rounded-[18px] border border-slate-faint px-4 py-3.5 transition-all duration-200 active:scale-[0.99]',
          open ? 'bg-white/70 border-copper/25' : 'bg-white/45 hover:bg-white/60'
        )}
        aria-expanded={open}
      >
        <div className="flex items-start justify-between gap-3">
          <span className="text-[16px] font-semibold text-ink leading-snug">{q}</span>
          <span
            className={cn(
              'mt-0.5 shrink-0 text-[17px] text-copper/70 transition-transform duration-200',
              open ? 'rotate-45' : 'rotate-0'
            )}
            aria-hidden="true"
          >
            +
          </span>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="overflow-hidden"
            >
              <p className="mt-3 pr-8 text-[15px] leading-6 text-ink-muted">{a}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </button>
    </motion.div>
  )
}

/* ─── PAGE ─── */

export default function SupportClient() {
  return (
    <div className="min-h-screen bg-paper">

      {/* ── HEADER ── */}
      <header className="safe-top sticky top-0 z-40 border-b border-slate-faint/70 bg-paper/82 backdrop-blur-xl">
        <ContentContainer size="wide" className="flex h-12 items-center justify-between">
          <Link href="/" className="font-serif text-[19px] leading-none tracking-tight text-ink">
            The Marriage Check-In
          </Link>
          <Link
            href="/sign-in?redirectTo=/dashboard"
            className="flex items-center min-h-[44px] px-2 -mr-2 text-[14px] font-semibold text-ink-muted transition-colors hover:text-copper"
          >
            Sign in
          </Link>
        </ContentContainer>
      </header>

      <main className="pb-16">

        {/* ── HERO ── */}
        <section className="pt-12 sm:pt-16">
          <ContentContainer size="wide">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              transition={{ duration: 0.5, ease: 'easeOut' }}
            >
              <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-copper">
                Support
              </p>
              <h1 className="mt-2 font-serif text-[34px] leading-[0.9] tracking-[-0.035em] text-ink sm:text-[44px]">
                Help with your Check-In.
              </h1>
              <p className="mt-3 max-w-md text-[16px] leading-6 text-ink-muted">
                Quick answers about invites, privacy, refunds, and results. If something feels unclear, email us and we&apos;ll help.
              </p>
            </motion.div>
          </ContentContainer>
        </section>

        {/* ── SUPPORT CARDS ── */}
        <section className="pt-8 sm:pt-10">
          <ContentContainer size="wide">
            <div className="mt-0 space-y-3">

              {/* Contact */}
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={viewport}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="rounded-[18px] border border-slate-faint bg-white/50 p-5"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-copper">Contact</p>
                <p className="mt-2 text-[15px] leading-6 text-ink-muted">
                  Questions, feedback, invite issues, account help, or anything unclear — email us.
                </p>
                <a
                  href="mailto:marriagecheckin@gmail.com"
                  className="mt-2 inline-block text-[15px] font-semibold text-copper hover:underline"
                >
                  marriagecheckin@gmail.com
                </a>
                <p className="mt-1 text-[13px] text-ink-faint">
                  We usually respond within 24 hours.
                </p>
              </motion.div>

              {/* Partner invites */}
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={viewport}
                transition={{ duration: 0.4, ease: 'easeOut', delay: 0.06 }}
                className="rounded-[18px] border border-slate-faint bg-white/50 p-5"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-copper">Partner invites</p>
                <p className="mt-2 text-[15px] leading-6 text-ink-muted">
                  If your partner did not receive the invite, resend it from your dashboard or copy the invite link. They will answer the same conversation privately.
                </p>
              </motion.div>

              {/* Refunds */}
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={viewport}
                transition={{ duration: 0.4, ease: 'easeOut', delay: 0.12 }}
                className="rounded-[18px] border border-slate-faint bg-white/50 p-5"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-copper">Refunds</p>
                <p className="mt-2 text-[15px] leading-6 text-ink-muted">
                  Full refund within 30 days. No questions asked. Email us and we&apos;ll take care of it.
                </p>
              </motion.div>

            </div>
          </ContentContainer>
        </section>

        {/* ── FAQ ── */}
        <section className="pt-10 sm:pt-14">
          <ContentContainer size="wide">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              transition={{ duration: 0.45, ease: 'easeOut' }}
            >
              <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-ink-faint">
                Common questions
              </p>
              <h2 className="mt-2 font-serif text-[34px] leading-[0.9] tracking-[-0.035em] text-ink sm:text-[44px]">
                Before you start.
              </h2>
            </motion.div>

            <div className="mt-5 space-y-2.5">
              {SUPPORT_QUESTIONS.map((item, i) => (
                <FaqItem key={item.q} q={item.q} a={item.a} index={i} />
              ))}
            </div>
          </ContentContainer>
        </section>

        {/* ── STILL NEED HELP ── */}
        <section className="pt-8 sm:pt-10">
          <ContentContainer size="wide">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              transition={{ duration: 0.45, ease: 'easeOut' }}
            >
              <div className="rounded-[18px] border border-slate-faint bg-white/50 p-5 text-center">
                <p className="text-[16px] font-semibold text-ink">
                  Still need help?
                </p>
                <p className="mt-2 text-[15px] leading-6 text-ink-muted">
                  If something felt confusing or if you need help, tell us. We read every note.
                </p>
                <a
                  href="mailto:marriagecheckin@gmail.com"
                  className="mt-3 inline-block text-[15px] font-semibold text-copper hover:underline"
                >
                  marriagecheckin@gmail.com
                </a>
              </div>
            </motion.div>
          </ContentContainer>
        </section>

      </main>

      {/* ── FOOTER ── */}
      <footer className="safe-bottom mt-10 relative">
        <div className="mx-auto max-w-[120px] h-px bg-gradient-to-r from-transparent via-copper/30 to-transparent" aria-hidden="true" />
        <div className="border-t border-slate-faint mt-3">
          <ContentContainer size="wide" className="flex flex-col items-center gap-1.5 py-5">
            <p className="font-serif text-[17px] text-ink tracking-tight">The Marriage Check-In</p>
            <div className="flex items-center gap-3 text-[13px] text-ink-faint">
              <a
                href="https://www.instagram.com/themarriagecheckin"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow The Marriage Check-In on Instagram"
                className="hover:text-ink-muted transition-colors"
              >
                Instagram
              </a>
              <span>&middot;</span>
              <a
                href="https://www.tiktok.com/@marriagecheckin"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow The Marriage Check-In on TikTok"
                className="hover:text-ink-muted transition-colors"
              >
                TikTok
              </a>
            </div>
            <div className="flex items-center gap-2 text-[13px] text-ink-faint">
              <Link href="/support" className="hover:text-ink-muted transition-colors">Support</Link>
              <span>&middot;</span>
              <Link href="/privacy" className="hover:text-ink-muted transition-colors">Privacy</Link>
              <span>&middot;</span>
              <Link href="/terms" className="hover:text-ink-muted transition-colors">Terms</Link>
            </div>
            <p className="text-[11px] text-ink-faint mt-1">&copy; 2026 Glendale AI, LLC</p>
          </ContentContainer>
        </div>
      </footer>

    </div>
  )
}
